import { Download } from 'lucide-react';
import { Button, type ButtonProps } from './Button';

export interface ExportCsvButtonProps extends Omit<ButtonProps, 'children' | 'title'> {
  /** How many rows the click would write — 0 disables the button. */
  count: number;
  /** Plural noun for the tooltip: "expenses", "invoices", "clients". */
  noun: string;
  /** What the count is scoped to, for the tooltip: "with these filters". */
  scope?: string;
}

/**
 * The only "Export CSV" button.
 *
 * One label, one glyph, one disabled rule, one tooltip shape — the coherence
 * pass found the same button written five times with four tooltips and two
 * icons (`Download` on four screens, `FileText` on the fifth). The *placement*
 * still differs by page (header vs filter bar, documented in CLAUDE.md); what
 * it looks like and when it is dead must not.
 *
 * A zero-row export is disabled rather than hidden: a button that vanishes when
 * a filter matches nothing reads as a missing feature, and the user cannot tell
 * whether this screen has an export at all.
 */
export function ExportCsvButton({
  count,
  noun,
  scope = 'with these filters',
  variant = 'outline',
  size = 'sm',
  disabled,
  ...props
}: ExportCsvButtonProps) {
  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      disabled={disabled || count === 0}
      title={
        count === 0
          ? `Nothing to export ${scope}`
          : `Download ${count} ${count === 1 ? noun.replace(/s$/, '') : noun} as CSV — exactly the rows on screen, in this order`
      }
      {...props}
    >
      <Download className="h-4 w-4" aria-hidden />
      Export CSV
    </Button>
  );
}
